/**
 * AlphaSignal product analytics logger.
 * Fire-and-forget — never throws, never blocks UI.
 * No PII. Flat key-value properties only.
 */

import { supabase } from "@/integrations/supabase/client";
import { hasAnalyticsConsent } from "@/lib/consent";

interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

const SESSION_STORAGE_KEY = "as_session_id";

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage unavailable (e.g. incognito, SSR)
    return "unknown";
  }
}

async function track(eventName: string, properties?: EventProperties): Promise<void> {
  // Do not fire analytics without explicit user consent (GDPR / ePrivacy)
  if (!hasAnalyticsConsent()) return;

  try {
    const sessionId = getSessionId();

    // Get JWT — may be null if user is not authenticated
    let authHeader: string | undefined;
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (token) {
        authHeader = `Bearer ${token}`;
      }
    } catch {
      // No auth — that's fine, pre-auth events are allowed
    }

    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const url = `https://${projectId}.supabase.co/functions/v1/track-event`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
    };
    if (authHeader) {
      headers["Authorization"] = authHeader;
    }

    // Fire-and-forget — don't await in calling code
    fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({
        event_name: eventName,
        properties: properties ?? {},
        session_id: sessionId,
      }),
    }).catch(() => {
      // Silently ignore network errors
    });
  } catch {
    // Never propagate errors from analytics
  }
}

export const analytics = { track };
